import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { 
  FiHome, FiUsers, FiFileText, FiBarChart2, FiLogOut, FiHelpCircle,
  FiZap, FiChevronLeft, FiChevronRight, FiMenu, FiX, FiShield
} from 'react-icons/fi';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import SupportWidget from './components/SupportWidget';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import CreditBadge from './components/CreditBadge';
import AIStatusIndicator from './components/AIStatusIndicator';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import CampaignDetail from './pages/CampaignDetail';
import ContentLibrary from './pages/ContentLibrary';
import UsageDashboard from './pages/UsageDashboard';
import HelpCenter from './pages/HelpCenter';
import ContactSupport from './pages/ContactSupport';
import FounderDashboard from './pages/FounderDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import ContactUs from './pages/ContactUs';
import ReportIssueButton from './components/ReportIssueButton';

function AppRouter() {
  const location = useLocation();
  
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout><Dashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          <AppLayout><Clients /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/clients/:clientId" element={
        <ProtectedRoute>
          <AppLayout><ClientDetail /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId" element={
        <ProtectedRoute>
          <AppLayout><CampaignDetail /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/content" element={
        <ProtectedRoute>
          <AppLayout><ContentLibrary /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/usage" element={
        <ProtectedRoute>
          <AppLayout><UsageDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/founder" element={
        <ProtectedRoute>
          <FounderDashboard />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <AppLayout><HelpCenter /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/contact" element={
        <ProtectedRoute>
          <AppLayout><ContactSupport /></AppLayout>
        </ProtectedRoute>
      } />
      {/* Public Policy Pages (No Auth Required - For Razorpay Compliance) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border/50 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/50 rounded-lg flex items-center justify-center">
            <FiZap className="text-white" size={16} />
          </div>
          <span className="font-heading font-bold text-foreground">firstcut<span className="text-primary">.me</span></span>
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-muted-foreground hover:text-foreground"
        >
          {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 pt-16 lg:pt-0 ${
        sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          {children}
        </div>
      </div>
      
      <SupportWidget />
      <ReportIssueButton />
      <SessionTimeoutWarning />
    </div>
  );
}

function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // Founder email - only this user sees the Founder tab
  const FOUNDER_EMAIL = 'nishantvarma143@gmail.com';
  const isFounder = user?.email === FOUNDER_EMAIL;
  
  const navItems = [
    { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '/clients', icon: FiUsers, label: 'Clients' },
    { path: '/content', icon: FiFileText, label: 'Content Library' },
    { path: '/usage', icon: FiBarChart2, label: 'Usage' },
    { path: '/help', icon: FiHelpCircle, label: 'Help' },
    // Founder tab - only visible to founder
    ...(isFounder ? [{ path: '/founder', icon: FiShield, label: 'Founder', isFounder: true }] : []),
  ];

  const sidebarClasses = `
    fixed top-0 h-screen bg-card border-r border-border/50 flex flex-col z-50
    transition-all duration-300 ease-in-out
    ${collapsed ? 'w-20' : 'w-64'}
    ${mobileOpen ? 'left-0' : '-left-full lg:left-0'}
  `;

  return (
    <aside className={sidebarClasses}>
      {/* Logo */}
      <div className={`p-4 border-b border-border/30 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <Link to="/dashboard" className="flex items-center gap-3" onClick={onMobileClose}>
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/50 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20 flex-shrink-0">
            <FiZap className="text-white" size={20} />
          </div>
          {!collapsed && (
            <div>
              <span className="font-heading font-bold text-foreground text-lg">firstcut<span className="text-primary">.me</span></span>
              <p className="text-muted-foreground text-xs">Content Strategy</p>
            </div>
          )}
        </Link>
        
        {/* Collapse button - desktop only */}
        <button 
          onClick={onToggle}
          className="hidden lg:flex p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
        >
          {collapsed ? <FiChevronRight size={18} /> : <FiChevronLeft size={18} />}
        </button>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path === '/clients' && location.pathname.startsWith('/clients')) ||
            (item.path === '/clients' && location.pathname.startsWith('/campaigns'));
          
          // Special styling for Founder tab
          const founderStyle = item.isFounder 
            ? isActive 
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
              : 'text-amber-400/70 hover:bg-amber-500/10 hover:text-amber-400 border border-transparent hover:border-amber-500/20'
            : '';
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onMobileClose}
              className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                item.isFounder 
                  ? founderStyle
                  : isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={20} className={`flex-shrink-0 ${isActive ? '' : 'group-hover:scale-110 transition-transform'}`} />
              {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
            </Link>
          );
        })}
        
        {/* Subtle Credit Badge - only shows when low */}
        <div className={`mt-4 ${collapsed ? 'flex justify-center' : ''}`}>
          <CreditBadge collapsed={collapsed} />
        </div>
        
        {/* AI Status Indicator - only shows when not operational */}
        <div className={`mt-2 ${collapsed ? 'flex justify-center' : ''}`}>
          <AIStatusIndicator collapsed={collapsed} />
        </div>
      </nav>

      {/* User Section */}
      <div className="border-t border-border/30 p-3">
        {user && !collapsed && (
          <div className="mb-2 px-3 py-2 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full ring-2 ring-primary/20" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20">
                  <span className="text-primary font-semibold text-sm">{user.name?.charAt(0) || 'U'}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}
        
        {user && collapsed && (
          <div className="flex justify-center mb-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full ring-2 ring-primary/20" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20">
                <span className="text-primary font-semibold text-sm">{user.name?.charAt(0) || 'U'}</span>
              </div>
            )}
          </div>
        )}
        
        <button
          onClick={() => {
            onMobileClose();
            logout();
          }}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground w-full transition-colors ${collapsed ? 'justify-center' : ''}`}
          data-testid="logout-btn"
          title={collapsed ? 'Sign Out' : undefined}
        >
          <FiLogOut size={20} />
          {!collapsed && <span className="font-medium text-sm">Sign Out</span>}
        </button>
      </div>
    </aside>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
